import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Button, Form, Modal } from "react-bootstrap";
import swal from "sweetalert";
import BASE_URL from "../../assets/global/url";
import { Link, useNavigate, useParams } from "react-router-dom";
import useDecodeToken from "../../hooks/customHook/useDecodeToken";
import DatePicker from "react-datepicker";
import CustomDatePicker from "../../components/CustomDatePicker";
import TypedDateInput from "../../components/inputs/TypedDateInput";
import { checkCutoffDate } from "../../utils/cutoffDateValidation";
import {
  formatCurrency,
  formatToTwoDecimal,
} from "../../utils/numberFormatter";

const FixedAssetUpdate = ({ authrztn }) => {
  const userLoggedID = useDecodeToken();
  const navigate = useNavigate();
  const { id } = useParams();
  const [validated, setValidated] = useState(false);
  const [edit, setEdit] = useState(false);

  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const [forecast, setForecast] = useState([]);

  const [status, setStatus] = useState("");
  const [transactionCode, setTransactionCode] = useState("");
  const [date, setDate] = useState();
  const [expenses_id, setExpenses_id] = useState("");

  const [costPerUnit, setCostPerUnit] = useState(0);
  const [quantity, setQuantity] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [monthsToPay, setMonthsToPay] = useState(0);
  // const [deductedMonthsToPay, setDeductedMonthsToPay] = useState(0);
  const [depreciationAmount, setDepreciationAmount] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [currencyName, setCurrencyName] = useState("PHP");
  const [products, setProducts] = useState([]);

  const [approvedForecast, setApprovedForecast] = useState([]);

  const [approvedForecastValue, setApprovedForecastValue] = useState(0);
  const [paidCount, setPaidCount] = useState(0);
  const [isCutoffPosted, setIsCutoffPosted] = useState(false);
  const [productName, setProductName] = useState("");
  const [currencyList, setCurrencyList] = useState([]);
  const [currencyId, setCurrencyId] = useState("");
  const [selectedFixedAsset, setSelectedFixedAsset] = useState([]);

  const [modalAllowZeroDepreciation, setModalAllowZeroDepreciation] =
    useState(false);
  const isAllowZeroDepreciation = monthsToPay == 0 && depreciationAmount == 0;
  const [editModeAllowZeroDepreciation, setEditModeAllowZeroDepreciation] =
    useState(false);

  useEffect(() => {
    fetchAssets();
    // calculateDepreciationAmount();
    calculateTotalCost();
  }, [costPerUnit, quantity, monthsToPay, totalCost]);

  // function getTodayDate() {
  //   const today = new Date();
  //   return today.toISOString().split("T")[0];
  // }

  const getApprovedForecast = () => {
    axios
      .get(BASE_URL + "/fixedasset/getApprovedForecast", {
        params: {
          id: id,
        },
      })
      .then((res) => {
        console.log(res.data);
        const { fixedAssetForecast, paidItemCount, totalCost } = res.data;
        const sortedForecastDate = fixedAssetForecast?.sort(
          (a, b) => new Date(a.date) - new Date(b.date),
        );
        setApprovedForecast(sortedForecastDate);

        const total = fixedAssetForecast.reduce(
          (acc, curr) => (curr.isPaid ? acc + curr.amount : acc),
          0,
        );
        setApprovedForecastValue(totalCost - total);
        setPaidCount(monthsToPay - paidItemCount); // Count of rows where isPaid is true
      });
  };

  const reloadData = () => {
    axios
      .get(BASE_URL + "/fixedasset/getFixedAssetById", {
        params: {
          id: id,
        },
      })
      .then((res) => {
        setTransactionCode(res.data.transaction_code);
        setDate(res.data.date_depreciated);
        setExpenses_id(res.data.expenses_id);
        setCurrencyName(res.data.currency.currency_name);
        // setProductName(res.data.expense.product_name);
        setCostPerUnit(res.data.cost_per_unit);
        setQuantity(res.data.quantity);
        setTotalCost(res.data.total_cost);
        setMonthsToPay(res.data.static_months_to_pay);
        // setDeductedMonthsToPay(res.data.months_to_pay);
        setDepreciationAmount(res.data.depreciation_amount);
        setRemarks(res.data.remarks);
        setStatus(res.data.status);
        setIsCutoffPosted(res.data.isPosted);
        setProductName(res.data.product_name);
        setCurrencyId(res.data.currency_id);
        // setUnitPrice(res.data.unit_price);

        setEditModeAllowZeroDepreciation(
          res.data.static_months_to_pay === 0 &&
            res.data.depreciation_amount === 0,
        );
      });
  };

  useEffect(() => {
    if (status === "Approved") {
      getApprovedForecast();
    }
    reloadData();
  }, [status]);

  const onInputFloat = (e) => {
    e.target.value = e.target.value.replace(/[^0-9.]/g, "");
  };

  const removeComma = (num) => {
    return parseFloat(String(num).replace(/,/g, ""));
  };
  const calculateTotalCost = () => {
    setTotalCost(
      (removeComma(costPerUnit) || 0) * (removeComma(quantity) || 0),
    );
  };

  const fetchAssets = () => {
    axios
      .get(BASE_URL + "/fixedasset/fetchExpensesProducts", {
        params: { id: id },
      })
      .then((res) => {
        let newExpensesData = [];
        // console.log("Raw response data:", res.data);

        res.data.forEach((data) => {
          // console.log("Checking item:", data);

          // Case 1: No fixed asset exists or empty array
          if (!data.fixed_assets || data.fixed_assets.length === 0) {
            // console.log("Case 1: No fixed assets or empty array");
            newExpensesData.push(data);
          }
          // Case 2: Fixed asset exists with empty array
          else if (
            Array.isArray(data.fixed_assets) &&
            data.fixed_assets.length === 0
          ) {
            // console.log("Case 2: Empty fixed assets array");
            newExpensesData.push(data);
          }
          // Case 3: Fixed asset exists with Rejected status
          else if (
            Array.isArray(data.fixed_assets) &&
            data.fixed_assets.length > 0
          ) {
            const lastAsset = data.fixed_assets[data.fixed_assets.length - 1];
            if (lastAsset.status === "Rejected") {
              // console.log("Case 3: Last asset has Rejected status");
              newExpensesData.push(data);
            }
          }
        });

        // console.log("Filtered data:", newExpensesData);
        setProducts(newExpensesData);
      })
      .catch((error) => {
        console.error("Error fetching assets:", error);
      });
  };

  const handleProductChange = (e) => {
    setExpenses_id(e.target.value);

    const product = products.find(
      (p) => String(p.id) === String(e.target.value),
    );
    if (product) {
      setCostPerUnit(product.unitPrice);
      setCurrencyName(product.currency.currency_name);
      setQuantity(product.assetQuantity);
      setDate(product.expenses_date);
    }
  };

  const calculateForecast = () => {
    const forecastData = [];
    const startDate = new Date(date); // Convert the date string to a Date object
    const monthsToForecast = Math.ceil(parseFloat(monthsToPay)); // Ensure monthsToPay is rounded up

    // Set a fixed depreciation amount
    const fixedDepreciationAmount = parseFloat(
      String(depreciationAmount).replace(/,/g, ""),
    ); // Change this to your desired fixed amount

    // If zero depreciation is allowed, don't generate forecast
    if (modalAllowZeroDepreciation) {
      setForecast([]);
      setShow(true);
      return;
    }

    for (let i = 1; i <= monthsToForecast; i++) {
      const nextMonth = new Date(startDate);
      nextMonth.setMonth(startDate.getMonth() + i); // Add months
      forecastData.push({
        date: nextMonth.toISOString().split("T")[0], // Format to YYYY-MM-DD
        amount: fixedDepreciationAmount, // Use the fixed depreciation amount
      });
    }

    // Calculate total depreciation
    const totalDepreciation = fixedDepreciationAmount * monthsToForecast;

    // Calculate remaining amount
    const remainingAmount = totalCost - totalDepreciation;

    // If there's a remaining amount, adjust the last month's amount
    if (remainingAmount < 0) {
      // If total depreciation exceeds total cost, adjust the last entry
      forecastData[forecastData.length - 1].amount += remainingAmount; // Adjust last month's amount
    } else if (remainingAmount > 0) {
      // If there's a remaining amount, add it as an additional forecast entry
      const nextMonth = new Date(startDate);
      nextMonth.setMonth(startDate.getMonth() + monthsToForecast); // Add one more month
      forecastData.push({
        date: nextMonth.toISOString().split("T")[0], // Format to YYYY-MM-DD
        amount: remainingAmount, // Remaining amount
      });
    }

    setForecast(forecastData);
    setShow(true);
  };

  const handleCheckForecast = () => {
    // Set modal checkbox based on edit mode checkbox state instead of values
    setModalAllowZeroDepreciation(editModeAllowZeroDepreciation);
    calculateForecast();
    setShow(true);
  };

  const handleModalCheckboxChange = (checked) => {
    setModalAllowZeroDepreciation(checked);

    if (checked) {
      // Set values to 0 when checkbox is checked (UI only)
      setMonthsToPay(0);
      setDepreciationAmount(0);
      setForecast([]);
    } else {
      // Recalculate values when checkbox is unchecked
      handleMonthsToPay(monthsToPay || 1);
    }
  };

  const openModal = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      swal({
        icon: "error",
        title: "Fields are required",
        text: "Please fill in the red text fields.",
      });
    } else {
      if (Number(totalCost).toFixed(2) === "0.00") {
        swal({
          icon: "error",
          title: "Invalid Total Cost",
          text: "The total cost must be greater than zero. Please review your entries.",
        });
        return;
      }

      // Modified validation - only show alert if checkbox is NOT checked
      if (
        monthsToPay == 0 &&
        depreciationAmount == 0 &&
        !editModeAllowZeroDepreciation
      ) {
        swal({
          icon: "warning",
          title: "Invalid Months to Pay and Depreciation Amount",
          text: "Months to Pay and Depreciation Amount should be greater than zero.",
        });
        return;
      }

      // Modified validation - only check if checkbox is NOT checked
      if (Math.trunc(monthsToPay) === 0 && !editModeAllowZeroDepreciation) {
        swal({
          icon: "warning",
          title: "Invalid Months to Pay",
          text: "Months to Pay cannot be less than one",
        }).then(() => setMonthsToPay(0));
        return;
      }

      setModalAllowZeroDepreciation(editModeAllowZeroDepreciation);
      calculateForecast();
    }
    setValidated(true);
  };

  const handleApprove = () => {
    swal({
      title: "Approve this fixed asset?",
      text: "",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((confirmed) => {
      if (confirmed) {
        axios
          .post(`${BASE_URL}/fixedasset/approveFixedAsset`, null, {
            params: {
              id,
              userLoggedID,
            },
          })
          .then((res) => {
            if (res.status === 200) {
              window.location.reload();
              swal({
                title: "Success",
                text: "Fixed asset approved successfully",
                icon: "success",
                buttons: false,
                timer: 2000,
                dangerMode: true,
              }).then(() => {
                reloadData();
                setEdit(false);
                setShow(false);
              });
            }
          });
      }
    });
  };

  const handleReject = () => {
    swal({
      title: "Reject this fixed asset?",
      text: "",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((confirmed) => {
      if (confirmed) {
        axios
          .post(`${BASE_URL}/fixedasset/rejectFixedAsset`, null, {
            params: {
              id,
              userLoggedID,
            },
          })
          .then((res) => {
            if (res.status === 200) {
              swal({
                title: "Success",
                text: "Fixed asset rejected successfully",
                icon: "success",
                buttons: false,
                timer: 2000,
                dangerMode: true,
              }).then(() => {
                reloadData();
                setEdit(false);
              });
            }
          });
      }
    });
  };

  const handleSkip = (forecastId, formattedDate) => {
    swal({
      title: "Skip depreciation?",
      text: `Are you sure you want to skip depreciation for the month of ${formattedDate}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((confirmed) => {
      if (confirmed) {
        axios
          .post(`${BASE_URL}/fixedasset/skipDeduction`, null, {
            params: {
              id,
              forecastId,
            },
          })
          .then((res) => {
            if (res.status === 200) {
              swal({
                title: "Success",
                text: `Depreciation for the month of ${formattedDate} has been successfully skipped.`,
                icon: "success",
                buttons: false,
                timer: 2000,
                dangerMode: true,
              }).then(() => {
                reloadData();
                getApprovedForecast();
              });
            }
          });
      }
    });
  };

  const handleMonthsToPay = (value) => {
    if (value == ".") {
      setMonthsToPay((prev) => prev + ".");
    }
    let inputValue = String(value || 0).replace(/[^0-9.]/g, "");

    let [integerPart, decimalPart] = inputValue.split(".");

    if (integerPart) {
      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    let formattedValue =
      decimalPart !== undefined ? `${integerPart}.${decimalPart}` : integerPart;

    let formatAmount = inputValue.replace(/,/g, "");
    // Convert to number and format with commas
    let numericValue = parseFloat(formatAmount);

    setMonthsToPay(formattedValue);
    // Calculate depreciation amount based on months to pay
    if (totalCost && numericValue > 0) {
      const total = totalCost / numericValue;

      let [integerPart, decimalPart] = String(total).split(".");

      if (integerPart) {
        integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      }

      let formattedValue =
        decimalPart !== undefined
          ? `${integerPart}.${decimalPart}`
          : integerPart;

      setDepreciationAmount(formattedValue);
    } else {
      setDepreciationAmount(0);
    }
  };

  const handleDepriciationAmount = (value) => {
    if (value == ".") {
      setMonthsToPay((prev) => prev + ".");
    }
    let inputValue = String(value || 0).replace(/[^0-9.]/g, "");

    let [integerPart, decimalPart] = inputValue.split(".");

    if (integerPart) {
      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
    let formattedValue =
      decimalPart !== undefined ? `${integerPart}.${decimalPart}` : integerPart;

    let formatAmount = inputValue.replace(/,/g, "");
    // Convert to number and format with commas
    let numericValue = parseFloat(formatAmount);

    setDepreciationAmount(formattedValue);
    // Calculate months to pay based on depreciation amount
    if (numericValue > 0) {
      const total = totalCost / numericValue;

      setMonthsToPay(Math.ceil(total));
    } else {
      setMonthsToPay(0);
    }
  };

  // Reusable function for numbers
  const handleAmountChange = (value, setState) => {
    if (value === ".") {
      setState((prev) => (prev.includes(".") ? prev : prev + "."));
      return;
    }

    // Remove non-numeric and non-dot characters
    let inputValue = value.replace(/[^0-9.]/g, "");

    // Split integer and decimal
    let [integerPart, decimalPart] = inputValue.split(".");

    // ✅ Remove leading zeros from integer part only (keep at least one digit)
    if (integerPart) {
      integerPart = integerPart.replace(/^0+(?!$)/, "");
      // Add commas
      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    // Rebuild formatted value
    let formattedValue =
      decimalPart !== undefined ? `${integerPart}.${decimalPart}` : integerPart;

    setState(formattedValue);
  };

  const fetchCurrency = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/currency/fetchCurrency`);
      setCurrencyList(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  // Replaced by checkCutoffDate (utils/cutoffDateValidation): its "no cutoff"
  // warning was commented out, so it never warned.
  // const dateValidation = async (selectedDate, clearField) => {
  //   try {
  //     const res = await axios.get(`${BASE_URL}/cutoff/dateValidation`, {
  //       params: {
  //         date: selectedDate,
  //       },
  //     });
  //     // if (res.data == false) {
  //     //   swal({
  //     //     icon: "error",
  //     //     title: "Invalid Date Selection",
  //     //     text: "Please Create Cutoff for this Date",
  //     //   }).then(() => {
  //     //     clearField("");
  //     //   });
  //     // }
  //   } catch (error) {
  //     console.error(error);
  //   }
  // };

  useEffect(() => {
    fetchCurrency();
  }, []);

  useEffect(() => {
    handleMonthsToPay(monthsToPay);
  }, [totalCost]);

  // Custom input for DatePicker: typed as MM/dd/yyyy and shown as Oct/10/2026
  // (TypedDateInput), only while editing. Wrapped in useMemo so the input isn't
  // remounted on every render. Only a complete date reaches handleDateChange, so
  // the cutoff check doesn't run on a half-typed date.
  const CustomInput = useMemo(() => {
    const Input = React.forwardRef(
      (
        { value, onClick, onChange, onBlur, onFocus, onKeyDown, generateYears },
        ref,
      ) => (
        <TypedDateInput
          className="form-control p-3 w-100"
          onClick={(e) => {
            if (onClick) onClick(e);

            const date = new Date(value).getFullYear();

            if (generateYears) generateYears(date); // Reset/Initialize Year List based on selected date
          }}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          value={value}
          ref={ref}
          required
          disabled={!edit}
        />
      ),
    );

    Input.displayName = "CustomInput";
    return Input;
  }, [edit]);

  const handleEditModeCheckboxChange = (checked) => {
    setEditModeAllowZeroDepreciation(checked);
    // Also update the modal state
    setModalAllowZeroDepreciation(checked);

    if (checked) {
      // Set values to 0 when checkbox is checked (UI only, no DB update)
      setMonthsToPay(0);
      setDepreciationAmount(0);
    } else {
      // Recalculate values when checkbox is unchecked
      handleMonthsToPay(monthsToPay || 1);
    }
  };

  const handleSaveAsset = () => {
    setShow(false);

    let formatMonthsToPay = String(monthsToPay).replace(/,/g, "");
    let formatDepreciationAmount = String(depreciationAmount).replace(/,/g, "");

    swal({
      title: "Update this fixed asset?",
      text: "",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((confirmed) => {
      if (confirmed) {
        axios
          .post(`${BASE_URL}/fixedasset/updateFixedAsset`, null, {
            params: {
              date,
              expenses_id,
              costPerUnit: removeComma(costPerUnit),
              quantity: removeComma(quantity),
              totalCost,
              monthsToPay: formatMonthsToPay,
              depreciationAmount: formatDepreciationAmount,
              remarks,
              forecast,
              id,
              userLoggedID,
              productName,
              currencyId,
            },
          })
          .then((res) => {
            if (res.status === 200) {
              swal({
                title: "Success",
                text: "Fixed asset updated successfully",
                icon: "success",
                buttons: false,
                timer: 2000,
                dangerMode: true,
              }).then(() => {
                reloadData();
                setEdit(false);
                // Update edit mode checkbox after successful save
                setEditModeAllowZeroDepreciation(
                  formatMonthsToPay == 0 && formatDepreciationAmount == 0,
                );
              });
            } else {
              swal({
                title: "Something Went Wrong",
                text: "Please contact your support immediately",
                icon: "error",
                buttons: false,
                timer: 2000,
                dangerMode: true,
              });
            }
          });
      } else {
        setShow(true);
      }
    });
  };

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      <div className="w-100 p-2 d-flex flex-row justify-content-between">
        <div className="d-flex flex-column title-custom">
          <span className="fs-3">
            <Link to="/accounting/fixedAsset" className="text-dark mx-2">
              <i class="fa-solid fa-arrow-left"></i>
            </Link>
            PREVIEW FIXED ASSET
          </span>
          {/* <span>LOCAL ACCOUNTS RECEIVABLE</span> */}
        </div>
      </div>
      {status === "Approved" && (
        <div className="row mt-3 mx-auto">
          <div className="col-sm">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title">Product Current Value</h5>
                <p
                  className="card-text text-success amount "
                  style={{ textDecoration: "underline", cursor: "pointer" }}
                  onClick={handleCheckForecast}
                >
                  {formatCurrency(approvedForecastValue, currencyName)}
                </p>
                <p className="card-text">
                  from {formatCurrency(totalCost, currencyName)}
                </p>
              </div>
            </div>
          </div>
          <div className="col-sm">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title">Months Remaining</h5>
                <p
                  className="card-text text-success amount"
                  style={{ textDecoration: "underline", cursor: "pointer" }}
                  onClick={handleCheckForecast}
                >
                  {formatToTwoDecimal(paidCount)}
                </p>
                <p className="card-text">OF {monthsToPay} months</p>
              </div>
            </div>
          </div>
        </div>
      )}
      <Form noValidate validated={validated} onSubmit={openModal}>
        <div className="container-fluid mt-4">
          <div className="row mb-2">
            <div className="col-sm">
              <span>Transaction Code</span>
              <input
                type="text"
                name=""
                id=""
                required
                className="form-control p-3"
                readOnly
                onChange={(e) => setTransactionCode(e.target.value)}
                value={transactionCode}
              />
            </div>
            <div className="col-sm">
              <span>Date Depreciated</span>
              {/* <input
                type="date"
                name=""
                id=""
                required
                readOnly
                className="form-control p-3"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              /> */}
              {/* <div className="position-relative">
                <DatePicker
                  selected={date}
                  onChange={(date) => {
                    setDate(date);
                    dateValidation(date, setDate);
                  }}
                  dateFormat="MMM/dd/yyyy"
                  className="form-control p-3"
                  placeholderText={"mmm/dd/yyyy"}
                  customInput={<CustomInput />}
                />
                <i
                  class="fa-solid fa-calendar-week calendar-position"
                  style={{
                    right: "1rem",
                    top: "1.3rem",
                  }}
                ></i>
              </div> */}
              <CustomDatePicker
                selected={date ? new Date(date) : ""}
                handleDateChange={(date) => {
                  setDate(date);
                  // Replaced by checkCutoffDate (utils/cutoffDateValidation):
                  // dateValidation's warning is commented out, so it never warned
                  // dateValidation(date, setDate);
                  if (date) checkCutoffDate(date, "Depreciation Date");
                }}
                setter={setDate}
                CustomInput={CustomInput}
                isRequired={true}
                validated={validated}
                // Replaced by checkCutoffDate (utils/cutoffDateValidation)
                // dateValidation={dateValidation}
                dateValidation={(date) => {
                  checkCutoffDate(date, "Depreciation Date");
                }}
                iconTopOffset={"1.3rem"}
              />
            </div>
          </div>
          <div className="row mb-2">
            <div className="col-sm">
              {/* <span>Item Name</span>
              <select
                className="form-select p-3"
                onChange={handleProductChange}
                required
                disabled={!edit}
                value={expenses_id}
              >
                <option value="" selected disabled>
                  Select Item
                </option>

                {products.map((expenses, index) => (
                  <option key={index} value={expenses.id}>
                    {`${expenses.transaction_id} - ${expenses.product_name}`}
                  </option>
                ))}
              </select> */}
              <span>Product Name</span>
              <input
                type="text"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                className="form-control p-3"
                readOnly={!edit}
                required
              />
            </div>

            <div className="col-sm">
              <span>Currency</span>
              <select
                className="form-select p-3"
                value={currencyId}
                onChange={(e) => {
                  const currency = currencyList.find(
                    (item) => item.id === e.target.value,
                  );
                  setCurrencyName(currency.currency_name);
                  setCurrencyId(e.target.value);
                }}
                disabled={!edit}
                required
              >
                <option value="" disabled>
                  Select Currency
                </option>
                {currencyList.map((data) => (
                  <option key={data.id} value={data.id}>
                    {data.currency_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="row mb-2">
            <div className="col-sm">
              <span>Quantity</span>
              <input
                type="text"
                value={formatToTwoDecimal(quantity)}
                onChange={(e) =>
                  handleAmountChange(e.target.value, setQuantity)
                }
                readOnly={!edit}
                className="form-control"
                style={{
                  padding: `1rem ${validated ? "2rem" : "1rem"} 1rem 1rem`,
                }}
                required
              />
            </div>
            <div className="col-sm mb-2">
              <span>Cost Per Unit</span>
              <div className="mb-2">
                <input
                  type="text"
                  value={formatCurrency(costPerUnit, currencyName)}
                  onInput={onInputFloat}
                  readOnly={!edit}
                  onChange={(e) => {
                    // setCostPerUnit(e.target.value);
                    handleAmountChange(e.target.value, setCostPerUnit);
                  }}
                  className="form-control p-3"
                  id="inlineFormInputGroup"
                  required
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
          <div className="row mb-2">
            <div className="col-sm">
              <div className="row">
                {/* <div className="col-sm mb-2">
                  <span>Available to Depreciate Quantity</span>
                  <div className="input-group mb-2">
                    <input
                      type="text"
                      readOnly
                      onInput={onInputFloat}
                      value={quantity.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      onChange={(e) => setQuantity(e.target.value)}
                      className="form-control p-3"
                      id="inlineFormInputGroup"
                      required
                      placeholder="0.00"
                    />
                  </div>
                </div> */}
                <div className="col-sm">
                  <span>Total Cost</span>
                  <input
                    type="text"
                    required
                    readOnly
                    onInput={onInputFloat}
                    className="form-control p-3"
                    id="inlineFormInputGroup"
                    value={fetchCurrency(totalCost, currencyName)}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-sm mb-2">
              <span>Months to Pay</span>
              <div className="input-group mb-2">
                <input
                  type="text"
                  onInput={onInputFloat}
                  required
                  readOnly={!edit}
                  className="form-control p-3"
                  id="inlineFormInputGroup"
                  placeholder="0"
                  value={monthsToPay}
                  // onChange={(e) => {
                  //   const value = e.target.value;
                  //   setMonthsToPay(value);
                  //   // Calculate depreciation amount based on months to pay
                  //   if (totalCost && value > 0) {
                  //     setDepreciationAmount(totalCost / value);
                  //   } else {
                  //     setDepreciationAmount(0);
                  //   }
                  // }}
                  onChange={(e) => {
                    if (!editModeAllowZeroDepreciation) {
                      const cleanedValue = String(e.target.value).replace(
                        /^0+,|^0+/,
                        "",
                      ); // Remove leading zeros and comma
                      handleMonthsToPay(cleanedValue);
                    }
                  }}
                  // Prevent Decimal input
                  onKeyDown={(e) => {
                    if (e.key === ".") e.preventDefault();
                  }}
                />
              </div>
            </div>
            <div className="col-sm mb-2">
              <span>Depreciation Amount</span>
              <div className="input-group mb-2">
                <div className="input-group-prepend">
                  <div className="input-group-text h-100">{currencyName}</div>
                </div>
                <input
                  type="text"
                  required
                  readOnly={!edit || isAllowZeroDepreciation}
                  className="form-control p-3"
                  id="inlineFormInputGroup"
                  placeholder="0.00"
                  value={formatToTwoDecimal(depreciationAmount)}
                  onChange={(e) => {
                    if (!editModeAllowZeroDepreciation) {
                      const cleanedValue = String(e.target.value).replace(
                        /^0+,|^0+/,
                        "",
                      ); // Remove leading zeros and comma
                      handleDepriciationAmount(cleanedValue);
                    }
                  }}
                  onInput={onInputFloat}
                />
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="allowZeroDepreciationCheck"
                  checked={
                    edit
                      ? editModeAllowZeroDepreciation
                      : isAllowZeroDepreciation
                  }
                  onChange={(e) => {
                    if (edit) {
                      handleEditModeCheckboxChange(e.target.checked);
                    }
                  }}
                  disabled={!edit}
                />
                <label
                  className="form-check-label text-muted"
                  htmlFor="allowZeroDepreciationCheck"
                >
                  Zero depreciation amount allowed
                </label>
              </div>
            </div>
          </div>
          <div className="row mb-2">
            <div className="col-sm mb-2">
              <span>Remarks</span>
              <textarea
                cols="5"
                rows="5"
                className="form-control p-2"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                readOnly={!edit}
              ></textarea>
            </div>
          </div>
          <div className="button-container mt-5">
            <div className="">
              {status === "Pending" ? (
                <React.Fragment>
                  {edit ? (
                    <>
                      <Button
                        variant="outline-secondary"
                        className="me-3"
                        type="button"
                        onClick={() => {
                          setEdit(false);

                          reloadData();
                        }}
                      >
                        Cancel Edit
                      </Button>
                      <Button className="btn btn-primary" type="submit">
                        View
                      </Button>
                    </>
                  ) : (
                    <div className="d-flex flex-row gap-3 justify-content-end">
                      <div className="d-flex flex-row">
                        {authrztn.includes("FixedAssets-Edit") && (
                          <Button
                            className="btn btn-primary"
                            onClick={(e) => {
                              e.preventDefault();
                              setEdit(true);
                            }}
                            type="button"
                          >
                            Edit
                          </Button>
                        )}
                      </div>
                      <div className="d-flex flex-row justify-content-end">
                        <Button
                          variant="success"
                          type="button"
                          onClick={() => {
                            handleCheckForecast();
                          }}
                        >
                          Check Forecast
                        </Button>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ) : (
                <React.Fragment></React.Fragment>
              )}
            </div>
          </div>
        </div>
      </Form>

      <Modal
        size="lg"
        show={show}
        onHide={() => {
          handleClose();
          setSelectedFixedAsset([]);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Fixed Asset Forecast</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Show message when zero depreciation is allowed */}
          {modalAllowZeroDepreciation ? (
            <div className="alert alert-info">
              <i className="fa-solid fa-info-circle me-2"></i>
              This asset has zero depreciation amount. No forecast will be
              generated.
            </div>
          ) : (
            // Show forecast only when zero depreciation is not allowed
            <>
              {status === "Approved"
                ? approvedForecast.map((date, index) => (
                    <div key={index} className="mb-3">
                      <div className="card p-3 shadow-sm border-0 rounded">
                        <div className="row align-items-center">
                          <div className="col-sm">
                            <strong>Date:</strong>
                            <span className="text-muted">
                              {new Date(date.date).toLocaleString("default", {
                                month: "long",
                                year: "numeric",
                              })}
                              <span
                                className={`mx-3 badge ${
                                  date.status === "Paid"
                                    ? "bg-success"
                                    : date.status === "Skipped"
                                      ? "bg-warning"
                                      : "bg-danger"
                                }`}
                              >
                                {date.status === "Paid"
                                  ? "Depreciated"
                                  : date.status === "Skipped"
                                    ? "Skipped"
                                    : "Pending"}
                              </span>
                            </span>
                          </div>
                          <div className="col-sm">
                            <strong>Amount:</strong>{" "}
                            <span className="fw-bold">
                              {formatCurrency(date.amount, currencyName)}
                            </span>
                          </div>
                          <div
                            className="text-end"
                            style={{ width: "max-content" }}
                          >
                            {date.isPaid ? (
                              <div className="px-3">
                                <i className="fa-solid fa-check text-success fs-4"></i>
                              </div>
                            ) : (
                              !modalAllowZeroDepreciation && (
                                <button
                                  onClick={() => {
                                    setSelectedFixedAsset((prev) => [
                                      ...prev,
                                      date,
                                    ]);
                                  }}
                                  disabled={
                                    selectedFixedAsset.includes(date) ||
                                    date.isCreated
                                  }
                                  className="btn btn-warning"
                                >
                                  Pay
                                </button>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                : forecast.map((date, index) => (
                    <div key={index} className="mb-3">
                      <div className="card p-3 shadow-sm">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>Date:</strong>{" "}
                            {new Date(date.date).toLocaleString("default", {
                              month: "long",
                              year: "numeric",
                            })}
                          </div>
                          <div>
                            <strong>Amount:</strong>{" "}
                            {formatCurrency(date.amount, currencyName)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
            </>
          )}

          {/* Selected forecast section */}
          <div className="d-flex justify-content-between">
            {selectedFixedAsset.length > 0 && !modalAllowZeroDepreciation && (
              <>
                <span className="mt-2">
                  Selected: {selectedFixedAsset.length}
                </span>
                <div>
                  <button
                    className="btn btn-success"
                    onClick={() => {
                      const totalAmount = selectedFixedAsset.reduce(
                        (acc, value) => {
                          return acc + value.amount;
                        },
                        0,
                      );
                      navigate(
                        `/accounting/add-expenses/${currencyId}/${totalAmount}`,
                        {
                          state: {
                            ids: selectedFixedAsset.map((item) => item.id),
                          },
                        },
                      );
                    }}
                  >
                    Add Expense
                  </button>
                </div>
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          {status === "Pending" ? (
            <React.Fragment>
              {edit ? (
                <>
                  <Button
                    variant="outline-secondary"
                    className="me-3"
                    type="button"
                    onClick={() => {
                      handleClose();
                    }}
                  >
                    Close
                  </Button>
                  <Button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveAsset}
                  >
                    Update
                  </Button>
                </>
              ) : (
                <div className="d-flex flex-row justify-content-between">
                  <div className="d-flex flex-row">
                    {/* <Button
                      className="btn btn-primary"
                      onClick={(e) => {
                        e.preventDefault();
                        setEdit(true);
                      }}
                      type="button"
                    >
                      Edit
                    </Button> */}
                  </div>
                  {authrztn.includes("FixedAssets-Approve") && (
                    <div className="d-flex flex-wrap gap-3">
                      <div className="align-self-center">
                        {isCutoffPosted && (
                          <p className="text-danger mb-0">
                            Action is prohibited as the Depreciated date has
                            already been posted.
                          </p>
                        )}
                      </div>
                      <div className="d-flex flex-row-reverse gap-3 justify-content-end">
                        <Button
                          variant="success"
                          type="button"
                          onClick={() => {
                            handleApprove();
                          }}
                          disabled={isCutoffPosted}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="danger"
                          type="button"
                          onClick={() => {
                            handleReject();
                          }}
                          disabled={isCutoffPosted}
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </React.Fragment>
          ) : (
            <React.Fragment></React.Fragment>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FixedAssetUpdate;
